/** Subscription date/day-count helpers, shared so the header pill, the Setup
 *  Plan card and the Payments page all show the same numbers. */

/** Whole calendar days from today until `date`, in local time (never negative).
 *  Uses date-only midnights so a partial day never truncates a day off — e.g.
 *  today 04.06 and expiry 29.06 returns 25, matching how people count days. */
export function calendarDaysUntil(date: Date): number {
  const end = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((end - today) / 86_400_000));
}

/** On demand (pay-per-response, billing_mode 'turn') accounts have no Subscription,
 *  so `active` is false — label them "On demand", never "No plan". */
export function isOnDemand(status: { billing_mode?: string | null } | null | undefined): boolean {
  return status?.billing_mode === 'turn';
}

/** The account is paid for: a live Subscription, or On demand that billing has
 *  not paused (a suspended On demand account has run out of wallet balance). */
export function isPlanCovered(
  status: { active: boolean; billing_mode?: string | null; billing_suspended?: boolean } | null | undefined
): boolean {
  if (!status) return false;
  return isOnDemand(status) ? !status.billing_suspended : status.active;
}

/** The account has a paid plan right now — Assistant or No assistant. Same rule as
 *  the CRM dashboard counter: a live Subscription row in status 'active' (trials
 *  and past-due rows do not count), whatever the billing mode. */
export function hasPaidPlan(status: { active: boolean; status?: string | null } | null | undefined): boolean {
  return Boolean(status?.active && status.status === 'active');
}

/** On demand that billing has not paused — billed per response, so not planless. */
export function isActiveOnDemand(
  status: { billing_mode?: string | null; billing_suspended?: boolean } | null | undefined
): boolean {
  return isOnDemand(status) && !status?.billing_suspended;
}

/** Days left for a subscription status. Prefers the authoritative period-end
 *  instant (calendar-day count, so it agrees with the displayed expiry date),
 *  falling back to the backend's (truncated) days_left when it is absent. */
export function subscriptionDaysLeft(
  status: { days_left: number | null; current_period_end?: string | null } | null | undefined
): number | null {
  if (!status) return null;
  if (status.current_period_end) return calendarDaysUntil(new Date(status.current_period_end));
  return status.days_left;
}
